/**
 * The newest value of each series, written at the line's end.
 *
 * Drawn on a live chart (a source without history - `docs/plans/data-sources.md`,
 * "Live charts"), where the latest reading is the thing people came to read:
 * each visible series gets a dot on its last point and the value as bare text
 * beside it, in its own colour - no box, no outline (chosen 2026-10-01 over
 * boxed tags). Values that would overlap are pushed apart vertically, so two
 * series reading the same value still show two numbers.
 *
 * Only what is on screen is used - no request. A series whose newest value is
 * older than the chart's newest reading by more than `staleAfterS` is drawn
 * muted, with its age, because a dead sensor's last value is not a current
 * reading (the Live API's 180 s rule).
 */

import type uPlot from 'uplot';
import { getCssVar } from './chart-dom.ts';
import { formatNum } from './format.ts';
import type { LiveLayout } from '../../../state/plot-state.ts';

const dpr = (): number => window.devicePixelRatio || 1;

/** One series to tag: its index in the uPlot series list and its colour. */
export interface TagSeries {
  idx: number;
  color: string;
}

/** Where a series last had a value: its x and y, or null if it never did. */
export function lastPoint(
  xs: ArrayLike<number | null | undefined>,
  ys: ArrayLike<number | null | undefined>,
): { x: number; y: number } | null {
  for (let i = Math.min(xs.length, ys.length) - 1; i >= 0; i--) {
    const x = xs[i];
    const y = ys[i];
    if (x != null && y != null && Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  }
  return null;
}

/**
 * Vertical centres for tags of height `h`, as close to `wanted` as they can be
 * without overlapping (a `gap` between them) and inside `[top, bottom]`.
 * Returned in the same order as `wanted`.
 */
export function spreadTags(wanted: number[], h: number, gap: number, top: number, bottom: number): number[] {
  const order = wanted.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  const step = h + gap;
  const placed = order.map((o) => o.y);
  const lo = top + h / 2;
  const hi = bottom - h / 2;
  // Downwards: each tag at least one step below the previous one.
  for (let k = 0; k < placed.length; k++) {
    placed[k] = Math.max(placed[k]!, lo, k > 0 ? placed[k - 1]! + step : lo);
  }
  // Upwards: anything pushed past the bottom pulls the ones above it up.
  for (let k = placed.length - 1; k >= 0; k--) {
    placed[k] = Math.min(placed[k]!, hi, k < placed.length - 1 ? placed[k + 1]! - step : hi);
  }
  const out = new Array<number>(wanted.length);
  order.forEach((o, k) => (out[o.i] = placed[k]!));
  return out;
}

/** "45 s", "12 min", "3 h", "2 d" - the age of a reading, short. */
export function shortAge(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86400)} d`;
}

/** Room kept right of the plot for the values, in CSS pixels. */
export const TAG_ROOM_PX = 56;

/**
 * Keep a strip right of the plot area for the values: chart padding when there
 * is no right-hand axis, or - when there is - a wider gap between that axis and
 * its numbers, so the values never sit on the axis labels. Never a wider x
 * range: the scales stay the data's own, so zoom state and the x readout are
 * untouched.
 */
export function reserveTagRoom(opts: uPlot.Options): void {
  const right = (opts.axes ?? []).find((a) => a.side === 1);
  if (right) {
    right.gap = (right.gap ?? 5) + TAG_ROOM_PX;
    const size = right.size;
    right.size = typeof size === 'function'
      ? (u, values, axisIdx, cycleNum) => size(u, values, axisIdx, cycleNum) + TAG_ROOM_PX
      : (size ?? 50) + TAG_ROOM_PX;
    return;
  }
  const [t = null, r = null, b = null, l = null] = (opts.padding ?? []) as Array<uPlot.Padding[number]>;
  opts.padding = [t, typeof r === 'number' ? Math.max(r, TAG_ROOM_PX) : TAG_ROOM_PX, b, l];
}

/**
 * Draw the values. Call from a uPlot `draw` hook, after the series, so a value
 * sits on top of the line it belongs to. `xIsTime` decides whether a stale
 * series says its age (only a time axis has one).
 */
export function drawLastValueTags(
  u: uPlot,
  series: TagSeries[],
  opts: { xIsTime: boolean; staleAfterS?: number } = { xIsTime: true },
): void {
  const xs = u.data[0] as ArrayLike<number | null>;
  const { left, top, width, height } = u.bbox;
  const d = dpr();
  const staleAfter = opts.staleAfterS ?? 180;

  const items: Array<{ color: string; x: number; y: number; px: number; py: number; text: string }> = [];
  for (const s of series) {
    const meta = u.series[s.idx];
    if (!meta || meta.show === false || !meta.scale || !(meta.scale in u.scales)) continue;
    const p = lastPoint(xs, u.data[s.idx] as ArrayLike<number | null>);
    if (!p) continue;
    const py = u.valToPos(p.y, meta.scale, true);
    const px = u.valToPos(p.x, 'x', true);
    if (!Number.isFinite(py) || !Number.isFinite(px)) continue;
    items.push({ color: s.color, x: p.x, y: p.y, px, py, text: formatNum(p.y) });
  }
  if (!items.length) return;

  const newest = Math.max(...items.map((it) => it.x));
  const fontPx = Math.round(11 * d);
  const gap = 5 * d;
  const centres = spreadTags(
    items.map((it) => it.py),
    fontPx,
    2 * d,
    top,
    top + height,
  );

  const ctx = u.ctx;
  // The strip `reserveTagRoom` kept: up to the right axis's numbers, or the
  // canvas edge when there is no right axis.
  const limit = u.axes.some((a) => a.side === 1) ? left + width + TAG_ROOM_PX * d : ctx.canvas.width;
  ctx.save();
  ctx.font = `600 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  const halo = getCssVar('--surface') || '#1a1d23';
  const muted = getCssVar('--text-dim') || '#8a9099';

  items.forEach((it, i) => {
    const stale = opts.xIsTime && newest - it.x > staleAfter;
    const color = stale ? muted : it.color;
    const label = stale ? `${it.text} · ${shortAge(newest - it.x)} old` : it.text;
    const w = ctx.measureText(label).width;
    // Right of the point when it fits, else just left of it - over the line,
    // which the halo keeps readable.
    const right = it.px + gap + w <= limit - 2 * d;
    const cy = centres[i]!;

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(it.px, it.py, 2.5 * d, 0, Math.PI * 2);
    ctx.fill();

    ctx.textAlign = right ? 'left' : 'right';
    const tx = right ? it.px + gap : it.px - gap;
    ctx.strokeStyle = halo;
    ctx.lineWidth = 3 * d;
    ctx.strokeText(label, tx, cy);
    ctx.fillText(label, tx, cy);
  });
  ctx.restore();
}

/**
 * Every data series of a built chart, with the colour uPlot strokes it in - for
 * a host that adds tags to options it did not build (a dashboard tile, through
 * `decorate`). A fit (labelled "· fit") is not a reading and gets no tag.
 */
export function tagSeriesOf(u: uPlot): TagSeries[] {
  const out: TagSeries[] = [];
  for (let i = 1; i < u.series.length; i++) {
    const s = u.series[i]!;
    if (typeof s.label === 'string' && s.label.endsWith('· fit')) continue;
    const stroke = typeof s.stroke === 'function' ? s.stroke(u, i) : s.stroke;
    out.push({ idx: i, color: typeof stroke === 'string' ? stroke : getCssVar('--text') || '#e0e0e0' });
  }
  return out;
}

/* ── Live layouts ─────────────────────────────────────────── */

/**
 * How a live chart shows its newest readings (`style.live_layout`, chosen
 * 2026-10-01 from `docs/design/sketches/live-last-value.html`):
 * `end` - each value beside its line's end; `center` - the values large across
 * the middle, the lines veiled behind; `now` - the axis runs a window either
 * side of now, the lines stop at a NOW line in the middle and the values sit
 * on it.
 */
export type { LiveLayout };

export interface LiveOverlayOptions {
  xIsTime: boolean;
  layout: LiveLayout;
  /** `now` only: the NOW label on top of the line. */
  nowLabel: boolean;
  /** The clock, epoch seconds - for `now`. Null draws `now` as `end`. */
  nowS: number | null;
  staleAfterS?: number;
}

/** Room above the plot for the NOW label, in CSS pixels. */
export const NOW_LABEL_ROOM_PX = 22;

/** Keep room for what the layout draws outside the plot area. */
export function reserveLiveRoom(opts: uPlot.Options, layout: LiveLayout, nowLabel: boolean): void {
  if (layout === 'end') reserveTagRoom(opts);
  if (layout === 'now' && nowLabel) {
    const [t = null, r = null, b = null, l = null] = (opts.padding ?? []) as Array<uPlot.Padding[number]>;
    opts.padding = [typeof t === 'number' ? Math.max(t, NOW_LABEL_ROOM_PX) : NOW_LABEL_ROOM_PX, r, b, l];
  }
}

/** Draw the chosen layout. Call from a `draw` hook, after the series. */
export function drawLiveOverlay(u: uPlot, series: TagSeries[], opts: LiveOverlayOptions): void {
  if (opts.layout === 'center') {
    drawBigValues(u, series, opts);
    return;
  }
  if (opts.layout === 'now' && opts.xIsTime && opts.nowS != null) {
    const sc = u.scales.x;
    if (sc?.min != null && sc.max != null && opts.nowS >= sc.min && opts.nowS <= sc.max) {
      drawNowLine(u, series, opts.nowS, opts);
      return;
    }
  }
  drawLastValueTags(u, series, { xIsTime: opts.xIsTime, ...(opts.staleAfterS != null ? { staleAfterS: opts.staleAfterS } : {}) });
}

/** Each visible series' newest point, in canvas pixels, with its colour. */
function newestPoints(u: uPlot, series: TagSeries[]): Array<{ color: string; label: string; x: number; y: number; px: number; py: number }> {
  const xs = u.data[0] as ArrayLike<number | null>;
  const out: Array<{ color: string; label: string; x: number; y: number; px: number; py: number }> = [];
  for (const s of series) {
    const meta = u.series[s.idx];
    if (!meta || meta.show === false || !meta.scale || !(meta.scale in u.scales)) continue;
    const p = lastPoint(xs, u.data[s.idx] as ArrayLike<number | null>);
    if (!p) continue;
    const py = u.valToPos(p.y, meta.scale, true);
    const px = u.valToPos(p.x, 'x', true);
    if (!Number.isFinite(py) || !Number.isFinite(px)) continue;
    out.push({ color: s.color, label: typeof meta.label === 'string' ? meta.label : '', x: p.x, y: p.y, px, py });
  }
  return out;
}

/** `center`: the newest values large across the middle, the lines veiled. */
function drawBigValues(u: uPlot, series: TagSeries[], opts: LiveOverlayOptions): void {
  const items = newestPoints(u, series).slice(0, 4);
  if (!items.length) return;
  const { left, top, width, height } = u.bbox;
  const d = dpr();
  const ctx = u.ctx;
  const surface = getCssVar('--surface') || '#1a1d23';
  const muted = getCssVar('--text-dim') || '#8a9099';
  const staleAfter = opts.staleAfterS ?? 180;
  const newest = Math.max(...items.map((it) => it.x));

  ctx.save();
  // The veil: the lines stay as context, the numbers are what is read.
  ctx.globalAlpha = 0.62;
  ctx.fillStyle = surface;
  ctx.fillRect(left, top, width, height);
  ctx.globalAlpha = 1;

  const colW = width / items.length;
  const big = Math.max(18 * d, Math.min(64 * d, colW / 4.4, height / 3.2));
  const small = Math.max(10 * d, Math.round(big * 0.24));
  const cy = top + height / 2;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  items.forEach((it, i) => {
    const x = left + colW * (i + 0.5);
    const stale = opts.xIsTime && newest - it.x > staleAfter;
    ctx.fillStyle = stale ? muted : it.color;
    ctx.font = `600 ${Math.round(big)}px system-ui, -apple-system, sans-serif`;
    ctx.fillText(formatNum(it.y), x, cy + big * 0.32);
    ctx.fillStyle = muted;
    ctx.font = `500 ${small}px system-ui, -apple-system, sans-serif`;
    const sub = stale ? `${it.label} · ${shortAge(newest - it.x)} old` : it.label;
    ctx.fillText(sub, x, cy + big * 0.32 + small * 2.1);
  });
  ctx.restore();
}

/** `now`: a NOW line where the clock is, and each value on it. */
function drawNowLine(u: uPlot, series: TagSeries[], nowS: number, opts: LiveOverlayOptions): void {
  const { top, height } = u.bbox;
  const bottom = top + height;
  const d = dpr();
  const ctx = u.ctx;
  const accent = getCssVar('--accent') || '#4d7cff';
  const surface = getCssVar('--surface') || '#1a1d23';
  const muted = getCssVar('--text-dim') || '#8a9099';
  const xn = u.valToPos(nowS, 'x', true);

  ctx.save();
  // The line: solid accent with a soft halo, fading out toward the bottom.
  const fade = ctx.createLinearGradient(0, top, 0, bottom);
  fade.addColorStop(0, accent);
  fade.addColorStop(0.75, accent);
  fade.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.strokeStyle = fade;
  ctx.globalAlpha = 0.14;
  ctx.lineWidth = 6 * d;
  ctx.beginPath(); ctx.moveTo(xn, top); ctx.lineTo(xn, bottom); ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.lineWidth = 1.25 * d;
  ctx.beginPath(); ctx.moveTo(xn, top); ctx.lineTo(xn, bottom); ctx.stroke();
  // A tick where now meets the time axis.
  ctx.strokeStyle = accent;
  ctx.beginPath(); ctx.moveTo(xn, bottom); ctx.lineTo(xn, bottom + 5 * d); ctx.stroke();

  if (opts.nowLabel) {
    const fs = Math.round(10 * d);
    ctx.font = `700 ${fs}px system-ui, -apple-system, sans-serif`;
    const t = new Date(nowS * 1000).toISOString().slice(11, 19);
    const label = `NOW ${t}`;
    const padX = 6 * d;
    const h = fs + 7 * d;
    const w = ctx.measureText(label).width + padX * 2;
    const px = xn - w / 2;
    const py = top - h - 3 * d;
    ctx.fillStyle = accent;
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') ctx.roundRect(px, py, w, h, h / 2);
    else ctx.rect(px, py, w, h);
    ctx.fill();
    ctx.fillStyle = surface;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, xn, py + h / 2 + 0.5 * d);
  }

  // The values, on the line: a dot on each newest reading, a faint lead to
  // now (the arrival lag, not data), and the value just right of the line.
  const items = newestPoints(u, series);
  if (items.length) {
    const fontPx = Math.round(11 * d);
    const centres = spreadTags(items.map((it) => it.py), fontPx, 2 * d, top, bottom);
    const staleAfter = opts.staleAfterS ?? 180;
    ctx.font = `600 ${fontPx}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    items.forEach((it, i) => {
      const stale = opts.xIsTime && nowS - it.x > staleAfter;
      const color = stale ? muted : it.color;
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(it.px, it.py, 2.5 * d, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = 1 * d;
      ctx.setLineDash([2 * d, 3 * d]);
      ctx.beginPath(); ctx.moveTo(it.px, it.py); ctx.lineTo(xn, centres[i]!); ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      const text = stale ? `${formatNum(it.y)} · ${shortAge(nowS - it.x)} old` : formatNum(it.y);
      ctx.strokeStyle = surface;
      ctx.lineWidth = 3 * d;
      ctx.strokeText(text, xn + 8 * d, centres[i]!);
      ctx.fillText(text, xn + 8 * d, centres[i]!);
    });
  }
  ctx.restore();
}
